import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import useRevalidate from "@/lib/useRevalidate";
import { Card } from "@/components/ui/card";
import { Camera, Plus, TrendingUp, AlertTriangle, Wallet, Package } from "lucide-react";

const Stat = ({ label, value, icon: Icon, color, testid, sub }) => (
  <Card className="p-5 rounded-2xl border-stone-200" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</div>
        <div className="mt-2 font-mono text-2xl md:text-3xl font-semibold text-stone-900 tracking-tight">{value}</div>
        {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
      </div>
      <div className={`w-11 h-11 rounded-xl grid place-items-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </Card>
);

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Dashboard() {
  const { t, user } = useApp();
  const [s, setS] = useState(null);

  const load = () => api.get("/reports/summary").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useRevalidate(load);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("dashboard")}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mt-1" data-testid="dash-greeting">
            {t("hi")}, {user?.name || ""}
          </h1>
        </div>
        <div className="flex gap-3">
          <Link to="/photo-to-bill" data-testid="dash-photo-btn"
                className="h-12 px-5 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            <Camera className="w-4 h-4" /> {t("photo_bill")}
          </Link>
          <Link to="/bills/new" data-testid="dash-newbill-btn"
                className="h-12 px-5 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> {t("new_bill")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label={t("today_sales")} value={money(s?.today_sales)} icon={TrendingUp} color="bg-[#166534]" testid="stat-today-sales" sub={`${s?.bill_count_today || 0} ${t("bills")}`} />
        <Stat label={t("month_sales")} value={money(s?.month_sales)} icon={TrendingUp} color="bg-[#B45309]" testid="stat-month-sales" />
        <Stat label={t("pending_credit")} value={money(s?.pending_credit)} icon={Wallet} color="bg-amber-600" testid="stat-pending-credit" />
        <Stat label={t("low_stock")} value={s?.low_stock_count ?? "—"} icon={AlertTriangle} color="bg-red-600" testid="stat-low-stock" />
      </div>

      {user?.role === "owner" && (
        <Card className="p-5 rounded-2xl border-stone-200" data-testid="stat-stock-value">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("stock_value")}</div>
              <div className="mt-2 font-mono text-3xl font-semibold">{money(s?.stock_valuation)}</div>
            </div>
            <Package className="w-8 h-8 text-stone-400" />
          </div>
        </Card>
      )}

      {s?.low_stock_items?.length > 0 && (
        <Card className="p-5 rounded-2xl border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <div className="font-heading font-semibold">{t("low_stock")}</div>
          </div>
          <ul className="divide-y divide-stone-100" data-testid="low-stock-list">
            {s.low_stock_items.map((it) => (
              <li key={it.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="font-medium">{it.name_en}</div>
                  <div className="text-xs text-stone-500 font-gujarati">{it.name_gu}</div>
                </div>
                <div className="font-mono text-sm text-red-600">{it.stock} {it.unit}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
